import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Inbox as InboxIcon, ArrowUpRight, CheckCheck, AtSign, UserCheck, ShoppingCart, PackageCheck, Sun } from "lucide-react";
import { fmtDate } from "@/lib/workflow";
import { taskLink } from "@/components/TaskItem";

const KIND = {
  mention: { label: "Mention", icon: AtSign, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  assignment: { label: "Assignment", icon: UserCheck, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  order_request: { label: "Order request", icon: ShoppingCart, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  order_processed: { label: "Order processed", icon: PackageCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  my_day_reminder: { label: "My Day", icon: Sun, cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

export default function InboxPage() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [kind, setKind] = useState("all");
  const load = (k = kind) => api.get("/inbox", { params: k === "all" ? {} : { kind: k } }).then((r) => setData(r.data));
  useEffect(() => { load(kind); }, [kind]); // eslint-disable-line

  const markRead = async (i) => { if (!i.read) { await api.patch(`/inbox/${i.inbox_id}/read`); await load(); window.dispatchEvent(new Event("inbox-changed")); } };
  const readAll = async () => { await api.post("/inbox/read-all"); await load(); window.dispatchEvent(new Event("inbox-changed")); };
  const linkFor = (i) => i.link || (i.project_id ? taskLink(i) : "/");

  const TABS = [{ v: "all", l: "All" }, { v: "mention", l: "Mentions" }, { v: "assignment", l: "Assignments" }, { v: "order", l: "Orders" }, { v: "unread", l: "Unread" }];

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8" data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">Personal</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">Things from others or the system that need your attention.</p>
        </div>
        <Button variant="outline" onClick={readAll} disabled={!data.unread} data-testid="inbox-read-all-btn"><CheckCheck className="h-4 w-4 mr-2" />Mark all read ({data.unread})</Button>
      </div>

      <div className="mt-5 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 flex-wrap" data-testid="inbox-filter-tabs">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setKind(t.v)} data-testid={`inbox-tab-${t.v}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${kind === t.v ? "bg-[#2563EB] text-white" : "text-slate-600 hover:text-slate-900"}`}>
            {t.l}{t.v === "unread" && data.unread > 0 ? ` (${data.unread})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-2">
        {data.items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500" data-testid="inbox-empty">
            <InboxIcon className="h-6 w-6 mx-auto text-slate-400" />
            <p className="mt-3 text-sm">{kind === "unread" ? "No unread notifications." : "Nothing here. @mentions, task assignments and order updates land here."}</p>
          </div>
        ) : data.items.map((i) => {
          const k = KIND[i.kind] || KIND.mention;
          const Icon = k.icon;
          return (
            <Link key={i.inbox_id} to={linkFor(i)} onClick={() => markRead(i)} className={`block rounded-lg border p-4 transition hover:border-slate-400 ${i.read ? "border-slate-200 bg-white" : "border-blue-200 bg-blue-50/50"}`} data-testid={`inbox-item-${i.inbox_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] ${k.cls}`}><Icon className="h-3 w-3 mr-1" />{k.label}</Badge>
                  {i.project_number && <span className="font-mono bg-slate-900 text-white rounded px-1.5 py-0.5">{i.project_number}</span>}
                  {i.project_title && <span className="font-medium text-slate-800">{i.project_title}</span>}
                  {i.phase_name && <Badge variant="outline" className="text-[10px]">{i.phase_name}</Badge>}
                  {!i.read && <span className="h-2 w-2 rounded-full bg-blue-600" data-testid={`inbox-unread-dot-${i.inbox_id}`} />}
                </div>
                <span className="text-[11px] font-mono text-slate-400">{fmtDate(i.created_at)}</span>
              </div>
              {i.title && <p className="mt-2 text-sm font-semibold text-slate-900">{i.title}</p>}
              <p className="mt-0.5 text-sm text-slate-800"><strong>{i.from_user_name}</strong>: {i.text}</p>
              <span className="mt-1 inline-flex items-center text-xs text-blue-700">{i.action_id ? "Open action" : i.link ? "Open" : "Open project"} <ArrowUpRight className="h-3 w-3 ml-0.5" /></span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
